import { getStoredPrincipal } from '../utils/apiAuth'

const TENANT_OVERRIDE_KEY = 'offlineTenantKey'

const hasStorage = (): boolean => typeof globalThis !== 'undefined' && Boolean(globalThis.window?.localStorage)

const normalizeTenantKey = (value: string | null | undefined): string => {
  const raw = (value ?? '').trim()
  if (!raw) return 'default'

  const normalized = raw
    .toLowerCase()
    .replaceAll(/\s+/g, '-')
    .replaceAll(/[^a-z0-9._-]/g, '')
    .replaceAll(/-+/g, '-')
    .replaceAll(/^[-.]+|[-.]+$/g, '')

  return normalized.length > 0 ? normalized.slice(0, 64) : 'default'
}

export const getActiveTenantKey = (): string => {
  if (!hasStorage()) return 'default'

  try {
    const override = globalThis.window?.localStorage?.getItem(TENANT_OVERRIDE_KEY)
    if (override && override.trim().length > 0) {
      return normalizeTenantKey(override)
    }

    const principal = getStoredPrincipal()
    return normalizeTenantKey(principal?.tenantKey)
  } catch {
    return 'default'
  }
}

export const setActiveTenantKey = (tenantKey: string | null): void => {
  if (!hasStorage()) return
  try {
    if (!tenantKey || tenantKey.trim().length === 0) {
      globalThis.window?.localStorage?.removeItem(TENANT_OVERRIDE_KEY)
    } else {
      globalThis.window?.localStorage?.setItem(TENANT_OVERRIDE_KEY, normalizeTenantKey(tenantKey))
    }
  } catch {
    // ignore
  }
}

export const getTenantScopedKey = (baseKey: string, tenantKey = getActiveTenantKey()): string => (
  `${baseKey}::${tenantKey}`
)

// `actorId` (not `userId`) is the identity already treated as canonical for
// "who is this" elsewhere (App.tsx's header display, Start.tsx) — it's
// populated across every auth mode (legacy token, DB-backed token, full user
// auth), where `userId` is only ever set in the last of those. Used for
// per-user preferences (e.g. a personal /home widget selection) that must
// stay stable regardless of which auth mode issued the session.
export const getActiveUserId = (): string => {
  if (!hasStorage()) return 'anonymous'
  try {
    const principal = getStoredPrincipal()
    const raw = (principal?.actorId ?? principal?.userId ?? '').trim()
    return raw || 'anonymous'
  } catch {
    return 'anonymous'
  }
}

export const getUserScopedKey = (
  baseKey: string,
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): string => (
  `${baseKey}::${tenantKey}::${userId}`
)

export const OFFLINE_DB_BASE_NAME = 'sunday-offline-db'

export const getOfflineDbNameForTenant = (tenantKey = getActiveTenantKey()): string => (
  `${OFFLINE_DB_BASE_NAME}::${normalizeTenantKey(tenantKey)}`
)
